import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../api/client'
import { translateFintabloError } from '../fintabloErrors'
import { ROUTES } from '../routes'
import Button from './Button'
import Input from './Input'
import IntegrationEventList from './IntegrationEventList'
import { Card, InlineAlert, Skeleton, Status } from './ui'
import directionIcon from '../assets/figma/direction.svg'
import editIcon from '../assets/figma/edit.svg'
import '../styles/connector-hub.css'
import '../styles/scenario-editor.css'
import {
  CONNECTOR_NAMES,
  ScenarioDropdown,
  ScenarioFilterBody,
  ScenarioMappingBody,
  TRACKABLE_SYSTEM_TARGETS,
  exactCounterpartyCondition,
  fieldError,
  loadFilterDirectories,
  mappingOptions,
  normalizeFintabloStatuses,
  syncCrmStages,
  useFilterSources,
  useSavedProviderOptions,
  validFilter
} from './ScenarioEditorParts'

const SCENARIO_STATUSES = {
  draft: { label: 'Черновик', tone: 'neutral' },
  active: { label: 'Активен', tone: 'success' },
  paused: { label: 'Приостановлен', tone: 'warning' }
}

// Тумблер «Отслеживать» на работающем сценарии (ADR R19): подтверждение и
// пояснение к разовому «Изменить сделку».
const TRACKING_CONFIRM = 'Сделки, которые сценарий уже создал, получат текущее значение поля из CRM при следующем изменении сделки.'
const UPDATE_ACTION_NOTE = 'Срабатывает один раз на сделку. Чтобы переносить изменения каждый раз, включите «Отслеживать» у поля в сценарии создания'

function toForm (scenario, fields = []) {
  const filters = scenario.filters || {}
  return {
    name: scenario.name || '',
    direction: scenario.direction || '',
    trigger: {
      source_event: scenario.trigger?.source_event || null
    },
    filters: {
      version: 1,
      mode: filters.mode === 'filter' ? 'filter' : 'none',
      // «Отслеживаемое поле» убрано из редактора (ADR R19 §10): сохранённое
      // значение не показывается и не стирается — уходит обратно как было.
      tracked_field: filters.tracked_field || null,
      groups: Array.isArray(filters.groups)
        ? filters.groups.map(group => Array.isArray(group)
          ? group.map(condition => exactCounterpartyCondition(condition, fields))
          : group)
        : []
    },
    action: {
      type: scenario.action?.type || null,
      ...(scenario.action?.create_partners === true ? { create_partners: true } : {}),
      target_status: scenario.action?.target_status || null,
      tracked_system_targets: scenario.action?.tracked_system_targets || []
    },
    rules: (scenario.rules || []).map(rule => ({
      type: rule.type,
      active: rule.active !== false,
      config: rule.config || {}
    }))
  }
}

function triggerStepValidFor (form) {
  return Boolean(form?.trigger?.source_event)
}

function filterStepValidFor (form) {
  return triggerStepValidFor(form) && validFilter(form?.filters)
}

function actionStepValidFor (form) {
  return filterStepValidFor(form) && Boolean(
    ['create_deal', 'update_deal'].includes(form?.action?.type) &&
    (form.action.type !== 'create_deal' || form.action.target_status)
  )
}

function initialUnlockedStep (form) {
  if (actionStepValidFor(form)) return 4
  if (filterStepValidFor(form)) return 3
  if (triggerStepValidFor(form)) return 2
  return 1
}

function hasRequiredValues (form, schemas) {
  if (!form.name.trim() || !form.trigger.source_event) return false
  if (!validFilter(form.filters)) return false
  if (!['create_deal', 'update_deal'].includes(form.action?.type)) return false
  if (form.action.type === 'create_deal' && !form.action.target_status) return false
  if (
    form.action.type === 'update_deal' &&
    !form.rules.some(rule => rule.type === 'field_mapping' && rule.config?.source && rule.config?.target)
  ) return false
  return form.rules.every(rule => {
    if (rule.active === false) return true
    if (
      rule.type === 'field_mapping' &&
      !String(rule.config?.source || '').trim() &&
      !String(rule.config?.target || '').trim()
    ) return true
    const schema = schemas.find(item => item.type === rule.type)?.schema
    return (schema?.fields || []).filter(field => field.required).every(field => String(rule.config?.[field.key] ?? '').trim())
  })
}

function submittableForm (form) {
  const submitted = JSON.parse(JSON.stringify(form))
  submitted.rules = submitted.rules.filter(rule => {
    if (rule.type !== 'field_mapping') return true
    return String(rule.config?.source || '').trim() || String(rule.config?.target || '').trim()
  })
  return submitted
}

function issueLabel (count, type) {
  if (type === 'error') return count === 1 ? 'ошибка' : count > 1 && count < 5 ? 'ошибки' : 'ошибок'
  return count === 1 ? 'предупреждение' : count > 1 && count < 5 ? 'предупреждения' : 'предупреждений'
}

export default function ScenarioOverview ({ user }) {
  const { slug, scenarioId } = useParams()
  const [scenario, setScenario] = useState(null)
  const [form, setForm] = useState(null)
  const [fintabloStatuses, setFintabloStatuses] = useState([])
  const [scenarioFields, setScenarioFields] = useState({})
  const [liveFields, setLiveFields] = useState([])
  // Справочники значений условий фильтра — общие с выгрузкой прошлых периодов.
  const sources = useFilterSources(slug)
  const {
    providerOptions,
    sourceEmployees,
    loadProviderOptions,
    searchCounterparties,
    counterpartyChoices,
    reset: resetSources,
    apply: applySources
  } = sources
  const [validation, setValidation] = useState({ valid: null, errors: [], warnings: [] })
  const [events, setEvents] = useState({ loading: true, items: [], error: null })
  const [error, setError] = useState(null)
  const [stageSyncWarning, setStageSyncWarning] = useState(null)
  // Отказ провайдера не должен выглядеть как пустой список: перечисляем
  // источники, которые не загрузились, вместо молчаливой пустоты.
  const [degraded, setDegraded] = useState([])
  const [notice, setNotice] = useState(null)
  const [busy, setBusy] = useState(null)
  const [editingName, setEditingName] = useState(false)
  const [activeStep, setActiveStep] = useState(1)
  const requestId = useRef(0)
  const eventsRequestId = useRef(0)
  const busyRef = useRef(false)
  const savedSnapshot = useRef('')

  const loadScenario = useCallback(async () => {
    const currentRequest = ++requestId.current
    setScenario(null)
    setForm(null)
    setError(null)
    setStageSyncWarning(null)
    setDegraded([])
    setEditingName(false)
    resetSources()
    try {
      const failures = []
      // Необязательный источник: при отказе не роняем редактор, но называем его.
      const optional = (promise, label, fallback, describe) => promise.catch(err => {
        failures.push({ label, reason: describe ? describe(err) : null })
        return fallback
      })

      const nextStageSyncWarning = await syncCrmStages(slug, user?.id)
      const [result, statuses, fields, live] = await Promise.all([
        api.getScenarioEditor(scenarioId),
        user?.id
          ? optional(
            api.getFintabloStatuses(user.id),
            'Статусы Финтабло',
            { statuses: [] },
            err => translateFintabloError(err?.message)
          )
          : Promise.resolve({ statuses: [] }),
        optional(api.getScenarioFields(slug), 'Поля сценария', {}),
        optional(api.getLiveFields(slug), 'Поля источника', { fields: [] })
      ])
      const directories = result.connection_id
        ? await loadFilterDirectories(slug, result.connection_id, result.filters, live.fields || [], optional)
        : null
      if (currentRequest !== requestId.current) return
      if (slug === 'amocrm' && !(result.trigger?.options?.statuses || []).length) {
        throw new Error('Не удалось загрузить стадии amoCRM. Проверьте подключение и повторите.')
      }
      const nextForm = toForm(result, live.fields || [])
      setScenario(result)
      setForm(nextForm)
      setDegraded(failures)
      setStageSyncWarning(nextStageSyncWarning)
      savedSnapshot.current = JSON.stringify(nextForm)
      setValidation(result.validation || { valid: null, errors: [], warnings: [] })
      setFintabloStatuses(normalizeFintabloStatuses(statuses))
      setScenarioFields(fields)
      setLiveFields(live.fields || [])
      applySources(result.connection_id, directories)
      setActiveStep(initialUnlockedStep(nextForm))
    } catch (err) {
      if (currentRequest === requestId.current) setError(err.message)
    }
  }, [scenarioId, slug, user?.id, resetSources, applySources])

  const loadScenarioEvents = useCallback(() => {
    const currentRequest = ++eventsRequestId.current
    setEvents({ loading: true, items: [], error: null })
    api.getIntegrationEvents({ scenario_id: scenarioId, limit: 5 })
      .then(result => {
        if (currentRequest === eventsRequestId.current) setEvents({ loading: false, items: result.items || [], error: null })
      })
      .catch(err => {
        if (currentRequest === eventsRequestId.current) setEvents({ loading: false, items: [], error: err.message })
      })
  }, [scenarioId])

  useEffect(() => {
    loadScenario()
    return () => { requestId.current += 1 }
  }, [loadScenario])

  useEffect(() => {
    loadScenarioEvents()
    return () => { eventsRequestId.current += 1 }
  }, [loadScenarioEvents])

  const dirty = form ? JSON.stringify(form) !== savedSnapshot.current : false
  const schemas = scenario?.rule_schemas || []
  const locallyRunnable = form ? hasRequiredValues(form, schemas) : false

  useEffect(() => {
    if (!dirty || !form) return undefined
    const timer = window.setTimeout(() => {
      api.validateScenario(slug, form)
        .then(setValidation)
        .catch(() => {})
    }, 300)
    return () => window.clearTimeout(timer)
  }, [dirty, form, slug])

  useEffect(() => {
    if (!dirty) return undefined
    const beforeUnload = event => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [dirty])

  function changeForm (updater) {
    setNotice(null)
    setForm(current => updater(current))
  }

  // Включение на сценарии, который уже работал, меняет судьбу созданных им
  // сделок — спрашиваем (текст подтверждён владельцем 2026-10-12). Черновик
  // сделок ещё не создавал.
  function confirmTrackingOn (next) {
    return !next || scenario.status === 'draft' || window.confirm(TRACKING_CONFIRM)
  }

  function toggleRuleTracking (index, next) {
    if (!confirmTrackingOn(next)) return
    updateRule(index, current => ({ ...current, config: { ...current.config, tracked: next } }))
  }

  function toggleSystemTracking (target, next) {
    if (!confirmTrackingOn(next)) return
    changeForm(current => {
      const targets = (current.action.tracked_system_targets || []).filter(item => item !== target)
      return {
        ...current,
        action: { ...current.action, tracked_system_targets: next ? [...targets, target] : targets }
      }
    })
  }

  function setFilterMode (mode) {
    setActiveStep(current => Math.min(current, 2))
    changeForm(current => ({
      ...current,
      filters: {
        ...current.filters,
        mode,
        groups: mode === 'filter' && current.filters.groups.length === 0
          ? [[{ field: '', field_type: 'string', operator: '', value: '' }]]
          : mode === 'none' ? [] : current.filters.groups
      }
    }))
  }

  // Условие может приехать уже настроенным — из сохранённого сценария. Тогда
  // справочник нужен сразу, иначе поле откроется пустым списком.
  useSavedProviderOptions(form?.filters, liveFields, loadProviderOptions)

  function updateFilterCondition (groupIndex, conditionIndex, patch) {
    setActiveStep(current => Math.min(current, 2))
    changeForm(current => ({
      ...current,
      filters: {
        ...current.filters,
        groups: current.filters.groups.map((group, currentGroupIndex) =>
          currentGroupIndex === groupIndex
            ? group.map((condition, currentConditionIndex) =>
              currentConditionIndex === conditionIndex ? { ...condition, ...patch } : condition)
            : group)
      }
    }))
  }

  function addAndCondition () {
    setActiveStep(current => Math.min(current, 2))
    changeForm(current => {
      const groups = current.filters.groups.length
        ? current.filters.groups.map((group, index) => index === current.filters.groups.length - 1
          ? [...group, { field: '', field_type: 'string', operator: '', value: '' }]
          : group)
        : [[{ field: '', field_type: 'string', operator: '', value: '' }]]
      return { ...current, filters: { ...current.filters, mode: 'filter', groups } }
    })
  }

  function addOrCondition () {
    setActiveStep(current => Math.min(current, 2))
    changeForm(current => ({
      ...current,
      filters: {
        ...current.filters,
        mode: 'filter',
        groups: [...current.filters.groups, [{ field: '', field_type: 'string', operator: '', value: '' }]]
      }
    }))
  }

  function removeFilterCondition (groupIndex, conditionIndex) {
    setActiveStep(current => Math.min(current, 2))
    changeForm(current => {
      const groups = current.filters.groups
        .map((group, currentGroupIndex) => currentGroupIndex === groupIndex
          ? group.filter((_, currentConditionIndex) => currentConditionIndex !== conditionIndex)
          : group)
        .filter(group => group.length > 0)
      return {
        ...current,
        filters: {
          ...current.filters,
          mode: groups.length ? 'filter' : 'none',
          groups
        }
      }
    })
  }

  function leaveEditor (event) {
    if (dirty && !window.confirm('Есть несохранённые изменения. Выйти без сохранения?')) event.preventDefault()
  }

  function advanceStep (step) {
    const nextStep = step + 1
    setActiveStep(current => Math.max(current, nextStep))
    window.setTimeout(() => {
      document.querySelector(`[data-scenario-step="${nextStep}"]`)?.focus()
    }, 0)
  }

  function applyMutation (result, submitted) {
    const updatedAt = result.draft?.updated_at || result.scenario?.updated_at || scenario.draft?.updated_at
    savedSnapshot.current = JSON.stringify(submitted)
    setForm(submitted)
    setScenario(current => ({
      ...current,
      ...result,
      name: result.name || result.scenario?.name || submitted.name,
      status: result.status || result.scenario?.status || current.status,
      direction: submitted.direction,
      trigger: { ...current.trigger, ...submitted.trigger },
      rules: submitted.rules,
      draft: { saved: true, updated_at: updatedAt }
    }))
    setValidation(result.validation || { valid: true, errors: [], warnings: [] })
  }

  async function runCommand (action) {
    if (busyRef.current || !form) return
    if (action === 'start' && validation.warnings?.length) {
      const consequences = validation.warnings.map(item => `• ${item.message}`).join('\n')
      if (!window.confirm(`Запустить с предупреждениями?\n\n${consequences}`)) return
    }
    busyRef.current = true
    setBusy(action)
    setError(null)
    setNotice(null)
    const submitted = submittableForm(form)
    try {
      if (action === 'save') {
        const result = await api.saveScenario(Number(scenarioId), submitted, scenario.draft?.updated_at)
        applyMutation(result, submitted)
        const errorCount = result.validation?.errors?.length || 0
        setNotice(errorCount
          ? `Черновик сохранён. Для запуска исправьте ${errorCount} ${issueLabel(errorCount, 'error')}`
          : 'Черновик сохранён. Можно запускать')
      } else if (action === 'start') {
        const result = await api.startScenario(Number(scenarioId), {
          expected_updated_at: scenario.draft?.updated_at,
          scenario: submitted
        })
        applyMutation(result, submitted)
        setNotice('Сценарий запущен. Новые события будут обработаны автоматически.')
      } else if (action === 'pause') {
        const result = await api.pauseScenario(Number(scenarioId))
        setScenario(current => ({ ...current, status: result.scenario.status, draft: { ...current.draft, updated_at: result.scenario.updated_at || current.draft?.updated_at } }))
        setNotice('Сценарий остановлен')
      }
    } catch (err) {
      if (err.details) {
        setValidation(current => ({ ...current, valid: false, errors: err.details }))
        window.setTimeout(() => {
          const target = document.querySelector('.scenario-editor [aria-invalid="true"], .scenario-editor [data-validation-error="true"]')
          if (!target) return
          target.scrollIntoView?.({ block: 'center' })
          target.focus?.()
        }, 0)
      } else {
        setError(err.message)
      }
    } finally {
      busyRef.current = false
      setBusy(null)
    }
  }

  function addFieldMapping () {
    changeForm(current => ({
      ...current,
      rules: [...current.rules, { type: 'field_mapping', active: true, config: { source: '', target: '' } }]
    }))
  }

  function updateRule (index, updater) {
    changeForm(current => ({
      ...current,
      rules: current.rules.map((rule, ruleIndex) => ruleIndex === index ? updater(rule) : rule)
    }))
  }

  function removeRule (index) {
    changeForm(current => ({ ...current, rules: current.rules.filter((_, ruleIndex) => ruleIndex !== index) }))
  }

  // Экран ошибки заменяет страницу целиком, поэтому ссылка в хаб живёт и
  // здесь: иначе со сценария, которого нет, вернуться можно только кнопкой
  // браузера.
  if (error && !scenario) {
    return (
      <div className='scenario-editor'>
        <Link className='connector-hub__back' to={ROUTES.INTEGRATION(slug)}>К хабу {CONNECTOR_NAMES[slug] || slug}</Link>
        <InlineAlert title='Не удалось загрузить сценарий' tone='error'>
          <span>{error}</span>
          <button className='ui-button ui-button--ghost connector-hub__retry' type='button' onClick={loadScenario}>Повторить</button>
        </InlineAlert>
      </div>
    )
  }
  if (!scenario || !form) return <Skeleton className='connector-hub__header-skeleton' aria-label='Загрузка сценария' />

  const status = SCENARIO_STATUSES[scenario.status] || { label: 'Неизвестный статус', tone: 'neutral' }
  const crmName = CONNECTOR_NAMES[slug] || slug
  const fieldOptions = mappingOptions(scenarioFields, liveFields, form.action?.type)
  // Поле с `filterable: false` — только источник сопоставления: его значение
  // берётся из справочника, а не из сделки, и условию не на чем сработать.
  const filterFields = liveFields.filter(field => field.filterable !== false)
  // Тумблер «Отслеживать» — только в сценарии создания (ADR R19 §1).
  const trackingAvailable = form.action?.type === 'create_deal'
  // Сначала строки без тумблера («ID → Комментарий»), затем «Название» с
  // тумблером — рядом с пользовательскими (владелец, 2026-10-13).
  const systemRows = [...fieldOptions.systemMappings].sort((a, b) =>
    Number(TRACKABLE_SYSTEM_TARGETS.has(a.targetField)) - Number(TRACKABLE_SYSTEM_TARGETS.has(b.targetField)))
  // «Изменить сделку» в новых сценариях не предлагается (решение владельца
  // 2026-10-12): изменения переносит тумблер. Сценарий, где действие уже
  // выбрано, сохраняет его.
  const actionOptions = [
    { value: 'create_deal', label: 'Создать сделку' },
    ...(scenario.action?.type === 'update_deal' ? [{ value: 'update_deal', label: 'Изменить сделку' }] : [])
  ]
  const systemMappingKeys = new Set(fieldOptions.systemMappings.map(mapping => `${mapping.source}:${mapping.targetField}`))
  const fieldMappingRules = form.rules
    .map((rule, index) => ({ rule, index }))
    .filter(item =>
      item.rule.type === 'field_mapping' &&
      !systemMappingKeys.has(`${item.rule.config?.source}:${item.rule.config?.target}`)
    )
  const isReadOnly = scenario.status === 'active'
  const errorCount = validation.errors?.length || 0
  const warningCount = validation.warnings?.length || 0
  const ruleErrors = (validation.errors || []).filter(issue => issue.field === 'rules')
  const ruleWarnings = (validation.warnings || []).filter(issue => issue.field === 'rules')
  const runDisabled = busy !== null || !locallyRunnable || validation.valid === false
  const triggerStepValid = triggerStepValidFor(form)
  const filterStepValid = filterStepValidFor(form)
  const actionStepValid = actionStepValidFor(form)
  const stepState = step => activeStep > step ? 'complete' : activeStep === step ? 'active' : 'inactive'
  const triggerEvent = scenario.trigger?.options?.events?.find(option => option.value === form.trigger.source_event)
  const triggerSummary = triggerEvent?.label || ''
  const filterConditionCount = form.filters?.groups?.reduce((total, group) => total + group.length, 0) || 0
  const filterSummary = form.filters?.mode === 'filter'
    ? `${filterConditionCount} ${filterConditionCount === 1 ? 'условие' : 'условия'}`
    : 'Без фильтров'
  const actionSummary = form.action?.type === 'create_deal'
    ? 'Создать сделку'
    : form.action?.type === 'update_deal' ? 'Изменить сделку' : ''
  const stepMarker = step => stepState(step) === 'complete'
    ? <span aria-label={`Шаг ${step} завершён`} className='scenario-editor__step-number'>✓</span>
    : <span className='scenario-editor__step-number'>{step}</span>

  return (
    <div className='scenario-editor'>
      <Link className='connector-hub__back' to={ROUTES.INTEGRATION(slug)} onClick={leaveEditor}>К хабу {crmName}</Link>

      <header className='scenario-editor__header'>
        <div className='scenario-editor__title-group'>
          <div className='scenario-editor__name-row'>
            {editingName && !isReadOnly
              ? (
                <>
                  <label className='ui-sr-only' htmlFor='scenario-name'>Название сценария</label>
                  <Input
                    autoFocus
                    id='scenario-name'
                    className='scenario-editor__name'
                    value={form.name}
                    error={Boolean(fieldError(validation, 'name'))}
                    onBlur={() => setEditingName(false)}
                    onChange={event => changeForm(current => ({ ...current, name: event.target.value }))}
                    onKeyDown={event => {
                      if (event.key === 'Enter') event.currentTarget.blur()
                    }}
                  />
                </>
                )
              : <h1 className='scenario-editor__name-text'>{form.name}</h1>}
            {!isReadOnly && (
              <Button
                aria-label='Редактировать название сценария'
                className='scenario-editor__edit-button'
                variant='ghost'
                onClick={() => setEditingName(true)}
              >
                <img className='scenario-editor__edit-icon' src={editIcon} alt='' />
              </Button>
            )}
          </div>
          {fieldError(validation, 'name') && <small className='scenario-editor__field-error'>{fieldError(validation, 'name').message}</small>}
          <div className='scenario-editor__meta'>
            <span className='scenario-editor__system-chip'>{crmName}</span>
            <img className='scenario-editor__direction-icon' src={directionIcon} alt='' />
            <span className='scenario-editor__system-chip'>Финтабло</span>
            {dirty && <span className='scenario-editor__dirty'>Есть несохранённые изменения</span>}
          </div>
        </div>
        <div className='scenario-editor__header-status'>
          <Status tone={status.tone}>{status.label}</Status>
        </div>
      </header>

      {degraded.length > 0 && (
        <InlineAlert title='Часть данных не загрузилась' tone='warning'>
          <ul className='scenario-editor__degraded'>
            {degraded.map(item => (
              <li key={item.label}>
                {item.label}{item.reason ? ` — ${item.reason}` : ''}
              </li>
            ))}
          </ul>
          <span>Списки на этих шагах будут пустыми, пока источник не ответит.</span>
        </InlineAlert>
      )}

      {stageSyncWarning && (
        <InlineAlert title='Не удалось обновить стадии amoCRM' tone='warning'>
          <span>{stageSyncWarning}</span>
          <button
            aria-label='Повторить обновление стадий'
            className='ui-button ui-button--ghost connector-hub__retry'
            type='button'
            onClick={loadScenario}
          >
            Повторить
          </button>
        </InlineAlert>
      )}

      <Card
        className='scenario-editor__section scenario-editor__step'
        aria-labelledby='scenario-trigger-heading'
        data-scenario-step='1'
        data-step-state={stepState(1)}
        tabIndex={-1}
      >
        <div className='scenario-editor__section-heading scenario-editor__step-heading'>
          {stepMarker(1)}
          <h2 id='scenario-trigger-heading'>Триггер</h2>
          {stepState(1) === 'complete' && <span className='scenario-editor__step-summary'>{triggerSummary}</span>}
        </div>
        <div className='scenario-editor__section-body'>
          <div className='scenario-editor__step-grid'>
            <label className='scenario-editor__field'>
              <span>Событие в {crmName}</span>
              <ScenarioDropdown
                aria-label={`Событие в ${crmName}`}
                disabled={activeStep < 1 || isReadOnly}
                options={scenario.trigger?.options?.events || []}
                placeholder='Выберите событие'
                searchable
                value={form.trigger.source_event || ''}
                onChange={event => {
                  const value = event.target.value
                  setActiveStep(current => Math.min(current, 1))
                  changeForm(current => ({
                    ...current,
                    trigger: { ...current.trigger, source_event: value || null }
                  }))
                }}
              />
            </label>
          </div>
          <div className='scenario-editor__step-footer'>
            <Button disabled={activeStep !== 1 || !triggerStepValid || isReadOnly} onClick={() => advanceStep(1)} variant='stroke'>Продолжить</Button>
          </div>
        </div>
      </Card>

      <Card
        className='scenario-editor__section scenario-editor__step'
        aria-labelledby='scenario-filter-heading'
        data-scenario-step='2'
        data-step-state={stepState(2)}
        tabIndex={-1}
      >
        <div className='scenario-editor__section-heading scenario-editor__step-heading'>
          {stepMarker(2)}
          <h2 id='scenario-filter-heading'>Фильтр</h2>
          {stepState(2) === 'complete' && <span className='scenario-editor__step-summary'>{filterSummary}</span>}
        </div>
        <div className='scenario-editor__section-body'>
          <ScenarioFilterBody
            activeStep={activeStep}
            addAndCondition={addAndCondition}
            addOrCondition={addOrCondition}
            counterpartyChoices={counterpartyChoices}
            crmName={crmName}
            filterFields={filterFields}
            form={form}
            isReadOnly={isReadOnly}
            liveFields={liveFields}
            loadProviderOptions={loadProviderOptions}
            providerOptions={providerOptions}
            removeFilterCondition={removeFilterCondition}
            searchCounterparties={searchCounterparties}
            setFilterMode={setFilterMode}
            slug={slug}
            sourceEmployees={sourceEmployees}
            statuses={scenario.trigger?.options?.statuses || []}
            triggerStepValid={triggerStepValid}
            updateFilterCondition={updateFilterCondition}
          />
          <div className='scenario-editor__step-footer'>
            <Button disabled={activeStep !== 2 || !filterStepValid || isReadOnly} onClick={() => advanceStep(2)} variant='stroke'>Продолжить</Button>
          </div>
        </div>
      </Card>

      <Card
        className='scenario-editor__section scenario-editor__step'
        aria-labelledby='scenario-action-heading'
        data-scenario-step='3'
        data-step-state={stepState(3)}
        tabIndex={-1}
      >
        <div className='scenario-editor__section-heading scenario-editor__step-heading'>
          {stepMarker(3)}
          <h2 id='scenario-action-heading'>Действие</h2>
          {stepState(3) === 'complete' && <span className='scenario-editor__step-summary'>{actionSummary}</span>}
        </div>
        <div className='scenario-editor__section-body'>
          <div className='scenario-editor__step-grid'>
            <label className='scenario-editor__field'>
              <span>Действие в Финтабло</span>
              <ScenarioDropdown
                aria-label='Действие в Финтабло'
                disabled={activeStep < 3 || !filterStepValid || isReadOnly}
                options={actionOptions}
                placeholder='Выберите действие'
                searchable
                value={form.action?.type || ''}
                onChange={event => {
                  setActiveStep(current => Math.min(current, 3))
                  const type = event.target.value || null
                  // Тумблер «Отслеживать» есть только в сценарии создания: при
                  // другом действии его флаги снимаются, иначе они остались бы
                  // в скрытых тумблерах и сервер отклонил бы сценарий.
                  changeForm(current => ({
                    ...current,
                    action: { type, target_status: null, tracked_system_targets: [] },
                    rules: type === 'create_deal'
                      ? current.rules
                      : current.rules.map(rule => {
                        if (rule.config?.tracked === undefined) return rule
                        const { tracked, ...config } = rule.config
                        return { ...rule, config }
                      })
                  }))
                }}
              />
            </label>
            {form.action?.type === 'create_deal' && (
              <label className='scenario-editor__field'>
                <span>Создать в стадии Финтабло</span>
                <ScenarioDropdown
                  aria-label='Создать в стадии Финтабло'
                  disabled={activeStep < 3 || isReadOnly}
                  options={fintabloStatuses}
                  placeholder='Выберите стадию'
                  searchable
                  value={form.action.target_status || ''}
                  onChange={event => {
                    setActiveStep(current => Math.min(current, 3))
                    changeForm(current => ({
                      ...current,
                      action: { ...current.action, target_status: event.target.value || null }
                    }))
                  }}
                />
              </label>
            )}
          </div>
          {form.action?.type === 'update_deal' && (
            <>
              <p className='scenario-editor__action-scope-note'>
                Изменение коснётся только сделок, созданных этой интеграцией
              </p>
              <p className='scenario-editor__action-scope-note'>
                {UPDATE_ACTION_NOTE}
              </p>
            </>
          )}
          <div className='scenario-editor__step-footer'>
            <Button disabled={activeStep !== 3 || !actionStepValid || isReadOnly} onClick={() => advanceStep(3)} variant='stroke'>Продолжить</Button>
          </div>
        </div>
      </Card>

      <Card
        className='scenario-editor__section scenario-editor__step'
        aria-labelledby='scenario-rules-heading'
        data-scenario-step='4'
        data-step-state={stepState(4)}
        data-validation-error={ruleErrors.length > 0 || undefined}
        tabIndex={-1}
      >
        <div className='scenario-editor__section-heading scenario-editor__step-heading'>
          {stepMarker(4)}
          <h2 id='scenario-rules-heading'>Маппинг полей</h2>
        </div>
        <div className='scenario-editor__section-body'>
          <ScenarioMappingBody
            actionStepValid={actionStepValid}
            activeStep={activeStep}
            addFieldMapping={addFieldMapping}
            crmName={crmName}
            fieldMappingRules={fieldMappingRules}
            fieldOptions={fieldOptions}
            form={form}
            isReadOnly={isReadOnly}
            liveFields={liveFields}
            removeRule={removeRule}
            ruleErrors={ruleErrors}
            ruleWarnings={ruleWarnings}
            slug={slug}
            systemRows={systemRows}
            toggleCounterpartyCreation={next => changeForm(current => ({ ...current, action: { ...current.action, create_partners: next } }))}
            toggleRuleTracking={toggleRuleTracking}
            toggleSystemTracking={toggleSystemTracking}
            trackingAvailable={trackingAvailable}
            updateRule={updateRule}
            validation={validation}
          />
        </div>
      </Card>

      <div className='scenario-editor__action-bar' role='group' aria-label='Действия сценария'>
        <span
          className='scenario-editor__readiness'
          data-tone={errorCount ? 'error' : warningCount ? 'warning' : 'success'}
        >
          {errorCount
            ? `⊗ ${errorCount} ${issueLabel(errorCount, 'error')}`
            : warningCount
              ? `△ ${warningCount} ${issueLabel(warningCount, 'warning')}`
              : isReadOnly
                ? '✓ Сценарий активен'
                : '✓ Готов к запуску'}
        </span>
        {error && <span className='scenario-editor__action-feedback' role='alert'>{error}</span>}
        {notice && <span className='scenario-editor__action-feedback' role='status'>{notice}</span>}
        {scenario.status === 'active'
          ? <Button className='scenario-editor__stop' loading={busy === 'pause'} disabled={busy !== null} onClick={() => runCommand('pause')}>Остановить</Button>
          : <Button variant='stroke' loading={busy === 'start'} disabled={runDisabled} onClick={() => runCommand('start')}>Запустить</Button>}
        {!isReadOnly && <Button loading={busy === 'save'} disabled={busy !== null} onClick={() => runCommand('save')}>Сохранить черновик</Button>}
      </div>

      <Card className='scenario-editor__section scenario-editor__events-section' aria-labelledby='scenario-events-heading'>
        <div className='scenario-editor__section-heading'><div><h2 id='scenario-events-heading'>Последние события</h2><p>Только события этого сценария.</p></div></div>
        <IntegrationEventList error={events.error} items={events.items} loading={events.loading} onRetry={loadScenarioEvents} />
      </Card>
    </div>
  )
}
